import { router, useForm } from "@inertiajs/react";
import {
  FileText,
  Image,
  MessageSquare,
  Paperclip,
  Plus,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import Dropzone from "shadcn-dropzone";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { ACCEPTED_FILE_TYPES, FILE_UPLOAD_CONFIG } from "@/constants/file";
import type { Comment } from "@/types";
import { formatDateTimeAR } from "@/utils/date";
import { formatFileSize } from "@/utils/file";

interface Props {
  comments: Comment[];
  commentsPath: string;
  resourceLabel?: string;
  compact?: boolean;
}

export default function CommentsThread({
  comments,
  commentsPath,
  resourceLabel = "este recurso",
  compact = true,
}: Props) {
  const { data, setData, processing, reset } = useForm({
    content: "",
    attachments: [] as File[],
  });
  const [error, setError] = useState<string | null>(null);
  const [expandedInput, setExpandedInput] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);

  const visibleComments = useMemo(() => {
    if (showAll) return comments;
    return comments.slice(0, 5);
  }, [comments, showAll]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const content = (data.content || "").trim();
    if (content.length === 0) {
      setError("El comentario no puede estar vacío");
      return;
    }

    const formData = new FormData();
    formData.append("comment[content]", content);
    data.attachments.forEach((file) => {
      formData.append("comment[attachments][]", file);
    });

    router.post(commentsPath, formData, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setExpandedInput(false);
      },
      onError: () => {
        setError("No se pudo guardar el comentario");
      },
    });
  };

  const handleFileDrop = (acceptedFiles: File[]) => {
    const currentAttachments = data.attachments;
    const availableSlots =
      FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS - currentAttachments.length;
    const filesToAdd = acceptedFiles.slice(0, availableSlots);

    if (filesToAdd.length > 0) {
      setData("attachments", [...currentAttachments, ...filesToAdd]);
      setAttachmentDialogOpen(false);
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = data.attachments.filter((_, i) => i !== index);
    setData("attachments", newAttachments);
  };

  const displayName = (c: Comment) =>
    c.user.full_name ?? c.user.name ?? c.user.email;

  const renderAttachments = (comment: Comment) => {
    if (!comment.attachments || comment.attachments.length === 0) return null;

    const attachmentCount = comment.attachments.length;
    const hasImages = comment.attachments.some((att) =>
      att.content_type?.startsWith("image/")
    );
    const hasPDFs = comment.attachments.some((att) =>
      att.content_type?.startsWith("application/pdf")
    );

    return (
      <div className="mt-2">
        <Accordion type="single" collapsible>
          <AccordionItem value="attachments" className="border-none">
            <AccordionTrigger className="py-2 px-0 text-sm font-medium text-muted-foreground hover:text-foreground">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                <span>Ver archivos adjuntos ({attachmentCount})</span>
                <div className="flex items-center gap-1 ml-2">
                  {hasImages && <Image className="h-3 w-3" />}
                  {hasPDFs && <FileText className="h-3 w-3" />}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-0">
              <div className="space-y-2">
                {comment.attachments.map((attachment, index) => {
                  const isImage = attachment.content_type?.startsWith("image/");

                  return (
                    <div
                      key={index}
                      className="p-2 border rounded-lg bg-muted/20"
                    >
                      <div className="flex items-center gap-2">
                        {isImage ? (
                          <Image className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        )}
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 truncate max-w-48"
                        >
                          {attachment.filename}
                        </a>
                        <span className="text-xs text-muted-foreground">
                          ({formatFileSize(attachment.byte_size)})
                        </span>
                      </div>
                      {isImage && attachment.url && (
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          className="mt-2 max-w-64 max-h-32 rounded border object-cover"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="inline-flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Comentarios
            <span className="text-muted-foreground text-xs font-normal">
              ({comments.length})
            </span>
          </span>
          {!expandedInput && (
            <Button size="sm" className="whitespace-nowrap" onClick={() => setExpandedInput(true)}>
              <Plus className="h-4 w-4 mr-1" /> Comentar
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Usa comentarios para coordinar faltantes, dudas o avisos. Todos con
          acceso a {resourceLabel} pueden verlos.
        </p>

        {comments.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin comentarios todavía.
          </p>
        ) : (
          <ul className="space-y-3">
            {visibleComments.map((c) => (
              <li
                key={c.id}
                className="grid grid-cols-[auto_1fr] gap-2 items-start"
              >
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] text-muted-foreground shrink-0">
                  {displayName(c).slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground truncate max-w-[60vw] sm:max-w-none">
                      {displayName(c)}
                    </span>
                    <span>·</span>
                    <time className="shrink-0" title={c.created_at}>
                      {formatDateTimeAR(c.created_at)}
                    </time>
                  </div>
                  <div
                    className={`text-sm mt-0.5 break-words ${compact ? "" : "leading-relaxed"}`}
                  >
                    {c.content}
                  </div>
                  {renderAttachments(c)}
                </div>
              </li>
            ))}
          </ul>
        )}

        {comments.length > 5 && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "Ver menos" : `Ver todos (${comments.length - 5} más)`}
            </Button>
          </div>
        )}

        {expandedInput && (
          <Card variant="outline">
            <CardHeader>
              <CardTitle>Agregar comentario</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-2">
                <TextareaWithCounter
                  placeholder="Escribe un comentario..."
                  value={data.content}
                  onChange={(e) => setData("content", e.target.value)}
                  maxLength={500}
                  showCharacterCount={true}
                />

                {/* Mostrar archivos adjuntos seleccionados */}
                {data.attachments.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                      Archivos adjuntos ({data.attachments.length}/
                      {FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS}):
                    </div>
                    {data.attachments.map((file, index) => (
                      <div
                        key={index}
                        className="p-2 border rounded-lg bg-muted/20"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {file.type?.startsWith("image/") ? (
                              <Image className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <FileText className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-sm font-medium truncate max-w-48">
                              {file.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({formatFileSize(file.size)})
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                            className="h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {error && <p className="text-xs text-destructive">{error}</p>}

                <div className="flex items-center justify-between">
                  <Dialog
                    open={attachmentDialogOpen}
                    onOpenChange={setAttachmentDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          data.attachments.length >=
                          FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS
                        }
                      >
                        <Paperclip className="h-4 w-4 mr-1" />
                        {data.attachments.length === 0
                          ? "Adjuntar archivos"
                          : data.attachments.length >=
                              FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS
                            ? "Máximo alcanzado"
                            : "Agregar más archivos"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Adjuntar archivos</DialogTitle>
                        <DialogDescription>
                          Sube imágenes o PDFs (máximo{" "}
                          {FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS} archivos
                          total)
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Dropzone
                          onDrop={handleFileDrop}
                          accept={{
                            ...ACCEPTED_FILE_TYPES.PDF_AND_IMAGES,
                          }}
                          maxFiles={
                            FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS -
                            data.attachments.length
                          }
                          disabled={
                            processing ||
                            data.attachments.length >=
                              FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS
                          }
                          containerClassName="w-full"
                          dropZoneClassName="group relative w-full h-32 rounded-lg border border-dashed border-border outline-none bg-background hover:bg-muted/50 transition-colors"
                        >
                          {(dz) => (
                            <div className="flex h-full w-full items-center justify-center">
                              <div className="text-center">
                                <div className="text-sm font-medium">
                                  {dz.isDragActive
                                    ? "Suelta los archivos aquí"
                                    : data.attachments.length >=
                                        FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS
                                      ? "Máximo de archivos alcanzado"
                                      : "Arrastra archivos aquí o haz clic"}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  PDF o imágenes (máx. 10MB c/u) -{" "}
                                  {data.attachments.length}/
                                  {FILE_UPLOAD_CONFIG.MAX_COMMENT_ATTACHMENTS}
                                </p>
                              </div>
                            </div>
                          )}
                        </Dropzone>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setExpandedInput(false);
                        setError(null);
                        reset();
                      }}
                    >
                      <X className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                    <Button type="submit" size="sm" disabled={processing}>
                      <Send className="h-4 w-4 mr-2" /> Enviar
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
